import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { AlertTriangle } from "lucide-react";
import InvoiceUploader, { InvoiceLinks } from "@/components/InvoiceUploader";

const ONES = ["", "one", "two", "three", "four", "five", "six", "seven", "eight", "nine", "ten", "eleven", "twelve", "thirteen", "fourteen", "fifteen", "sixteen", "seventeen", "eighteen", "nineteen"];
const TENS = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];
const two = (n) => (n < 20 ? ONES[n] : `${TENS[Math.floor(n / 10)]}${n % 10 ? "-" + ONES[n % 10] : ""}`);
const three = (n) => [n >= 100 ? `${ONES[Math.floor(n / 100)]} hundred` : "", two(n % 100)].filter(Boolean).join(" ");

export function amountInWords(value) {
  const num = Number(value);
  if (!num || num < 0 || !isFinite(num)) return "";
  let n = Math.floor(num);
  const paise = Math.round((num - n) * 100);
  const parts = [];
  const crore = Math.floor(n / 1e7); n %= 1e7;
  const lakh = Math.floor(n / 1e5); n %= 1e5;
  const thousand = Math.floor(n / 1000); n %= 1000;
  if (crore) parts.push(`${crore >= 100 ? amountInWords(crore).replace(/ rupees.*$/i, "").toLowerCase() : two(crore)} crore`);
  if (lakh) parts.push(`${two(lakh)} lakh`);
  if (thousand) parts.push(`${two(thousand)} thousand`);
  if (n) parts.push(three(n));
  let s = parts.join(" ") || "zero";
  s = `${s.charAt(0).toUpperCase()}${s.slice(1)} rupees`;
  if (paise) s += ` and ${two(paise)} paise`;
  return `${s} only`;
}

export const inr = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const FIELD_LABELS = { receipt_date: "Date", payer_name: "Payer", project_id: "Project", tranche_ref: "Tranche", bank_reference: "Bank ref / UTR", amount: "Amount", is_recovery: "Type", is_80g: "80G", is_fcra: "FCRA", is_interest_income: "Interest income", notes: "Remarks", attachment_file_ids: "Attachment", status: "Status" };

const blank = { project_id: "", payer_name: "", tranche_ref: "", bank_reference: "", amount: "", receipt_date: new Date().toISOString().slice(0, 10), is_80g: false, is_fcra: false, is_interest_income: false, is_recovery: false, notes: "", attachment_file_ids: [] };

export function ReceiptForm({ projects, onSaved, isRecovery, receipt }) {
  const editing = !!receipt;
  const [f, setF] = useState(editing ? { ...blank, ...receipt, project_id: receipt.project_id || "", amount: receipt.amount } : { ...blank, is_recovery: !!isRecovery });
  const [files, setFiles] = useState((editing ? receipt.attachment_file_ids || [] : []).map(id => ({ id })));
  const [reason, setReason] = useState("");
  const [warnings, setWarnings] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => { setF({ ...f, [k]: v }); setWarnings(null); };
  const body = () => ({ ...f, amount: parseFloat(f.amount || 0), project_id: f.project_id || null, attachment_file_ids: files.map(x => x.id) });

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    if (editing && reason.trim().length < 5) { toast.error("Give a reason for the change (at least 5 characters)"); return; }
    setBusy(true);
    try {
      if (warnings === null) {
        const w = (await api.post("/fund-receipts/check", { ...body(), id: receipt?.id })).data.warnings || [];
        if (w.length) { setWarnings(w); setBusy(false); return; }
      }
      if (editing) await api.patch(`/fund-receipts/${receipt.id}`, { ...body(), reason });
      else await api.post("/fund-receipts", body());
      toast.success(editing ? "Receipt updated" : (f.is_recovery ? "Recovery recorded" : "Receipt recorded"));
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); setBusy(false); }
  };

  const words = amountInWords(f.amount);
  return (
    <>
      <DialogHeader><DialogTitle>{editing ? "Edit Fund Receipt" : (isRecovery ? "Record Recovery" : "New Fund Receipt")}</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-3 mt-3 max-h-[75vh] overflow-y-auto pr-1" data-testid="receipt-form">
        <div>
          <Label>Project (leave empty for general fund)</Label>
          <Select value={f.project_id} onValueChange={(v) => set("project_id", v)}>
            <SelectTrigger data-testid="receipt-project-select"><SelectValue placeholder="General" /></SelectTrigger>
            <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Payer / Donor</Label><Input required value={f.payer_name} onChange={(e) => set("payer_name", e.target.value)} data-testid="receipt-payer-input" /></div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Amount</Label>
            <Input required type="number" step="0.01" min="0.01" value={f.amount} onChange={(e) => set("amount", e.target.value)} data-testid="receipt-amount-input" />
          </div>
          <div><Label>Receipt Date</Label><Input required type="date" value={f.receipt_date} onChange={(e) => set("receipt_date", e.target.value)} data-testid="receipt-date-input" /></div>
        </div>
        {words && <p className="text-xs text-slate-600 -mt-1 italic" data-testid="receipt-amount-words">{words}</p>}
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Tranche</Label><Input value={f.tranche_ref} onChange={(e) => set("tranche_ref", e.target.value)} placeholder="1st, 2nd…" data-testid="receipt-tranche-input" /></div>
          <div><Label>Bank reference / UTR</Label><Input value={f.bank_reference} onChange={(e) => set("bank_reference", e.target.value)} data-testid="receipt-utr-input" /></div>
        </div>
        {editing && (
          <div>
            <Label>Type</Label>
            <Select value={f.is_recovery ? "recovery" : "grant"} onValueChange={(v) => set("is_recovery", v === "recovery")}>
              <SelectTrigger data-testid="receipt-type-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="grant">Grant</SelectItem><SelectItem value="recovery">Recovery</SelectItem></SelectContent>
            </Select>
          </div>
        )}
        {!f.is_recovery && (
          <div className="flex flex-wrap gap-4 text-sm">
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.is_80g} onChange={(e) => set("is_80g", e.target.checked)} data-testid="receipt-80g-check" /> 80G eligible</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={f.is_fcra} onChange={(e) => set("is_fcra", e.target.checked)} data-testid="receipt-fcra-check" /> FCRA</label>
            <label className="flex items-center gap-2" title="Populates Annexure A row 3 in ICAI UC"><input type="checkbox" checked={f.is_interest_income} onChange={(e) => set("is_interest_income", e.target.checked)} data-testid="receipt-interest-check" /> Interest / other income on unutilised CSR funds</label>
          </div>
        )}
        <div><Label>Remarks</Label><Textarea value={f.notes || ""} onChange={(e) => set("notes", e.target.value)} data-testid="receipt-notes-input" /></div>
        <InvoiceUploader label="Attachment (optional)" files={files} onChange={(v) => { setFiles(v); setWarnings(null); }} testId="receipt-attachment" />
        {editing && <div><Label>Reason for change *</Label><Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Amount mistyped – bank statement shows ₹…" data-testid="receipt-reason-input" /></div>}
        {warnings?.length > 0 && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-2 space-y-1" data-testid="receipt-warnings">
            {warnings.map((w, i) => <div key={i} className="text-xs text-amber-900 flex gap-1.5" data-testid={`receipt-warning-${i}`}><AlertTriangle className="w-3.5 h-3.5 shrink-0" />{w}</div>)}
            <div className="text-[11px] text-amber-800">Check the figures. Click "Save anyway" if they are correct.</div>
          </div>
        )}
        <DialogFooter>
          <Button type="submit" disabled={busy} className={warnings?.length ? "bg-amber-600 hover:bg-amber-700" : ""} data-testid="save-receipt-btn">
            {busy ? "Saving…" : warnings?.length ? "Save anyway" : "Save"}
          </Button>
        </DialogFooter>
      </form>
    </>
  );
}

const show = (field, v, projects) => {
  if (v === null || v === undefined || v === "") return "—";
  if (field === "amount") return inr(v);
  if (field === "project_id") return projects.find(p => p.id === v)?.name || v;
  if (field === "is_recovery") return v ? "Recovery" : "Grant";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return `${v.length} file(s)`;
  return String(v);
};

export function ReceiptDetail({ r, projects, onEdit, onVoid }) {
  const h = r.history || [];
  return (
    <>
      <DialogHeader><DialogTitle>Receipt · {r.payer_name}</DialogTitle></DialogHeader>
      <div className="space-y-3 text-sm" data-testid="receipt-detail">
        {r.voided && <div className="rounded border border-rose-300 bg-rose-50 p-2 text-xs text-rose-800" data-testid="receipt-detail-voided">Voided – {r.void_reason} ({r.voided_by_name}, {String(r.voided_at || "").slice(0, 16).replace("T", " ")})</div>}
        <div className="grid grid-cols-2 gap-x-4 gap-y-1">
          <span className="text-slate-500">Date</span><span>{r.receipt_date}</span>
          <span className="text-slate-500">Project</span><span>{projects.find(p => p.id === r.project_id)?.name || "General"}</span>
          <span className="text-slate-500">Amount</span><span className="font-mono" data-testid="receipt-detail-amount">{inr(r.amount)}</span>
          <span className="text-slate-500">Tranche</span><span>{r.tranche_ref || "—"}</span>
          <span className="text-slate-500">Bank ref / UTR</span><span className="font-mono text-xs">{r.bank_reference || "—"}</span>
          <span className="text-slate-500">Remarks</span><span>{r.notes || "—"}</span>
        </div>
        <InvoiceLinks fileIds={r.attachment_file_ids} />
        <div className="rounded-md border p-2" data-testid="receipt-history">
          <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Change log</div>
          {h.length ? <ol className="space-y-2">{h.map((x, i) => (
            <li key={i} className="text-xs border-l-2 border-slate-200 pl-2" data-testid={`receipt-history-${i}`}>
              <div><b className="capitalize">{x.action}</b> · {x.by_name}{x.by_role ? ` (${x.by_role})` : ""} · <span className="text-slate-500">{String(x.at).slice(0, 16).replace("T", " ")}</span></div>
              <div className="text-slate-600">Reason: {x.reason}</div>
              {(x.changes || []).map((c, j) => <div key={j} className="text-slate-700">{FIELD_LABELS[c.field] || c.field}: <span className="line-through text-slate-400">{show(c.field, c.old, projects)}</span> → <b>{show(c.field, c.new, projects)}</b></div>)}
            </li>
          ))}</ol> : <p className="text-xs text-slate-500">No changes since it was entered.</p>}
        </div>
        {(r.can_edit || r.can_void) && (
          <DialogFooter className="gap-2">
            {r.can_void && <Button variant="outline" className="text-rose-700 border-rose-300" onClick={onVoid} data-testid="receipt-detail-void-btn">Void</Button>}
            {r.can_edit && <Button onClick={onEdit} data-testid="receipt-detail-edit-btn">Edit</Button>}
          </DialogFooter>
        )}
      </div>
    </>
  );
}

export function VoidForm({ r, onDone }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (reason.trim().length < 5) { toast.error("Give a reason (at least 5 characters)"); return; }
    setBusy(true);
    try { await api.post(`/fund-receipts/${r.id}/void`, { reason }); toast.success("Receipt voided"); onDone(); }
    catch (err) { toast.error(err.response?.data?.detail || "Failed"); setBusy(false); }
  };
  return (
    <>
      <DialogHeader><DialogTitle>Void receipt</DialogTitle></DialogHeader>
      <form onSubmit={submit} className="space-y-3" data-testid="void-form">
        <p className="text-sm text-slate-600">{r.payer_name} · {r.receipt_date} · {inr(r.amount)}. The receipt stays visible but is removed from all totals. This cannot be undone.</p>
        <div><Label>Reason *</Label><Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Entered twice" data-testid="void-reason-input" /></div>
        <DialogFooter><Button type="submit" disabled={busy} className="bg-rose-600 hover:bg-rose-700" data-testid="void-confirm-btn">{busy ? "Voiding…" : "Void receipt"}</Button></DialogFooter>
      </form>
    </>
  );
}
